import { Close } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextEditor } from '@work4all/components/lib/input/format-text/TextEditor/TextEditor';
import { createElementFromHTML } from '@work4all/components/lib/input/format-text/TextEditor/utils/html-parser';

import { useDevExpressReportSettings } from '../../../../../dev-express-logo-file-controller/hooks/use-dev-express-report-settings';

interface DevExpressFooterDialogProps {
  onClose: (updated: boolean) => void;
}

function hasStyleProp(
  element: Element
): element is Element & { style: CSSStyleDeclaration } {
  return 'style' in element;
}

const tableSelector =
  'table.no-border td, table[fr-original-class="no-border"] td';

export const DevExpressFooterDialog = (props: DevExpressFooterDialogProps) => {
  const { onClose } = props;
  const { mutate, res } = useDevExpressReportSettings();
  const [footerHtml, setFooterHtml] = useState(res.data.footerHtml);
  const { t } = useTranslation();

  const handleSave = useCallback(async () => {
    const body = createElementFromHTML(footerHtml);
    const tableCells = body.querySelectorAll(tableSelector);
    tableCells.forEach((cell) => {
      if (hasStyleProp(cell)) {
        cell.style.border = 'none';
      }
    });

    await mutate({
      variables: {
        footerHtml: body.innerHTML,
      },
    });
    onClose(true);
  }, [mutate, onClose, footerHtml]);

  useEffect(() => {
    const footerHtml = res.data.footerHtml;

    if (footerHtml) {
      const body = createElementFromHTML(footerHtml);
      const tableCells = body.querySelectorAll(tableSelector);
      tableCells.forEach((cell) => {
        if (hasStyleProp(cell)) {
          cell.style.border = undefined;
        }
      });
      setFooterHtml(body.innerHTML);
    }
  }, [res.data.footerHtml]);

  return (
    <Stack p="1rem" spacing="1rem">
      <Stack direction="row" width="100%" alignItems="center">
        <IconButton onClick={() => onClose(false)}>
          <Close />
        </IconButton>
        <Typography variant="h4" flex={1}>
          {t('SIMPLE_REPORT.PAGE_FOOTER')}
        </Typography>
        <Button variant="contained" onClick={handleSave}>
          {t('INPUTS.SAVE')}
        </Button>
      </Stack>
      <TextEditor value={footerHtml} onChange={(e) => setFooterHtml(e)} />
    </Stack>
  );
};
